import {
  Alert,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../../../auth/useAuth";
import { WIDGET_URL } from "../../../../constants";
import { getMonolithString } from "../../../../utils";
import { AUTH_COMPANY_NAME } from "../../../company/models";
import { CopyableTextField } from "../../../ui/components/CopyableTextField";
import {
  AppCategory,
  AppSettingsTabProps,
  IdentificationProvider,
  IndividualsScreeningProvider,
  TOnboardingConfig,
} from "../../models";
import { InnovatricsSettings } from "../InnovatricsSettings";
import { FineTuneLineData } from "../InnovatricsSettings/FineTuneLine";

const getCategoryLabel = (category: AppCategory) =>
  category === AppCategory.ENTITY ? "Entity" : "Individual";

const isProvider = (value: unknown, provider: string): boolean =>
  typeof value === "string" && value.toLowerCase() === provider.toLowerCase();

export const getProviderDisplayValue = <T extends string>(
  value: unknown,
  options: readonly T[],
): T | "" =>
  options.find((option) => isProvider(value, option)) || "";

export const getSelectedIndividualsScreeningProvider = (
  config: TOnboardingConfig,
): IndividualsScreeningProvider | undefined =>
  config.individuals_screening_provider;

export const normalizeIndividualsScreeningProvider = (
  identificationProvider: IdentificationProvider | undefined,
  screeningProvider?: IndividualsScreeningProvider,
): IndividualsScreeningProvider | undefined => {
  if (isProvider(identificationProvider, IdentificationProvider.MANUAL)) {
    return isProvider(screeningProvider, IndividualsScreeningProvider.MANUAL)
      ? screeningProvider
      : IndividualsScreeningProvider.MANUAL;
  }

  if (identificationProvider === undefined) {
    return screeningProvider;
  }

  return isProvider(screeningProvider, IndividualsScreeningProvider.MANUAL)
    ? IndividualsScreeningProvider.AMLBOT
    : screeningProvider;
};

export const getIndividualsScreeningProviderOptions = (
  identificationProvider: IdentificationProvider | undefined,
): IndividualsScreeningProvider[] =>
  isProvider(identificationProvider, IdentificationProvider.MANUAL)
    ? [IndividualsScreeningProvider.MANUAL]
    : [
        IndividualsScreeningProvider.AMLBOT,
        IndividualsScreeningProvider.SUMSUB,
      ];

export const updateIdentificationProviderConfig = (
  config: TOnboardingConfig,
  identificationProvider: IdentificationProvider,
): TOnboardingConfig => {
  if (config.category === AppCategory.ENTITY) {
    return {
      ...config,
      identification_provider: identificationProvider,
    };
  }

  return {
    ...config,
    identification_provider: identificationProvider,
    individuals_screening_provider: normalizeIndividualsScreeningProvider(
      identificationProvider,
      config.individuals_screening_provider,
    ),
  };
};

export const updateCategoryConfig = (
  config: TOnboardingConfig,
  category: AppCategory,
): TOnboardingConfig => {
  if (category === AppCategory.ENTITY) {
    return { ...config, category };
  }

  return {
    ...config,
    category,
    individuals_screening_provider: normalizeIndividualsScreeningProvider(
      config.identification_provider,
      config.individuals_screening_provider,
    ),
  };
};

const GeneralFields = ({
  publicKey,
  widgetUrl,
  category,
  setCategory,
  identificationProvider,
  setIdentificationProvider,
  individualsScreeningProvider,
  setIndividualsScreeningProvider,
  pocStellarVerifiableCreds,
  setPocStellarVerifiableCreds,
}: {
  publicKey: string;
  widgetUrl: string;
  category: AppCategory;
  setCategory: (value: AppCategory) => void;
  identificationProvider: IdentificationProvider | undefined;
  setIdentificationProvider: (value: IdentificationProvider) => void;
  individualsScreeningProvider: IndividualsScreeningProvider | undefined;
  setIndividualsScreeningProvider: (
    value: IndividualsScreeningProvider,
  ) => void;
  pocStellarVerifiableCreds: boolean;
  setPocStellarVerifiableCreds: (value: boolean) => void;
}) => {
  return (
    <Paper
      sx={{
        margin: "12px 0",
        padding: "20px 32px",
        background: "#ffffff",
        borderRadius: "14px",
        border: "none",
      }}
    >
      <Stack spacing={2}>
        <CopyableTextField label='Public key' value={publicKey} />
        <CopyableTextField label='Widget URL' value={widgetUrl} />
        <FormControl fullWidth variant='filled' sx={{ mt: 1 }}>
          <InputLabel id='app-category-label' shrink>
            App category
          </InputLabel>
          <Select
            labelId='app-category-label'
            id='app-category'
            value={category}
            label='App category'
            onChange={(e) => {
              setCategory(e.target.value as AppCategory);
            }}
          >
            <MenuItem value={AppCategory.INDIVIDUAL}>
              {getCategoryLabel(AppCategory.INDIVIDUAL)}
            </MenuItem>
            <MenuItem value={AppCategory.ENTITY}>
              {getCategoryLabel(AppCategory.ENTITY)}
            </MenuItem>
          </Select>
        </FormControl>
        <FormControl fullWidth sx={{ mt: 1 }}>
          <InputLabel id='identification-provider-label'>
            Identification provider
          </InputLabel>
          <Select
            labelId='identification-provider-label'
            id='identification-provider'
            value={getProviderDisplayValue(
              identificationProvider,
              Object.values(IdentificationProvider),
            )}
            label='Identification provider'
            variant='filled'
            onChange={(e) =>
              setIdentificationProvider(e.target.value as IdentificationProvider)
            }
          >
            <MenuItem value={IdentificationProvider.AMLBOT}>
              {IdentificationProvider.AMLBOT}
            </MenuItem>
            <MenuItem value={IdentificationProvider.INNOVATRICS}>
              {IdentificationProvider.INNOVATRICS}
            </MenuItem>
            <MenuItem value={IdentificationProvider.MANUAL}>
              {IdentificationProvider.MANUAL}
            </MenuItem>
          </Select>
        </FormControl>
        {category === AppCategory.INDIVIDUAL && (
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel id='individuals-screening-provider-label'>
              Individuals screening provider
            </InputLabel>
            <Select
              labelId='individuals-screening-provider-label'
              id='individuals-screening-provider'
              value={getProviderDisplayValue(
                individualsScreeningProvider,
                getIndividualsScreeningProviderOptions(identificationProvider),
              )}
              label='Individuals screening provider'
              variant='filled'
              onChange={(e) =>
                setIndividualsScreeningProvider(
                  e.target.value as IndividualsScreeningProvider,
                )
              }
            >
              {getIndividualsScreeningProviderOptions(identificationProvider).map(
                (provider) => (
                  <MenuItem key={provider} value={provider}>
                    {provider}
                  </MenuItem>
                ),
              )}
            </Select>
          </FormControl>
        )}
        {isProvider(identificationProvider, IdentificationProvider.MANUAL) && (
          <Alert severity='warning'>
            Shouldn't be used in production, only for testing and development purposes.
          </Alert>
        )}
        <FormControlLabel
          control={
            <Checkbox
              checked={pocStellarVerifiableCreds}
              onChange={(e) => setPocStellarVerifiableCreds(e.target.checked)}
            />
          }
          label='POC stellar verifiable creds'
        />
      </Stack>
    </Paper>
  );
};

const mapFineTuneFromConfig = (config: TOnboardingConfig): FineTuneLineData[] => {
  const fineTuning = config.innovatrics_settings?.document_types?.fine_tuning || [];
  return fineTuning.map((item: any, index: number) => ({
    id: `ft-${index}-${Date.now()}`,
    documentType: item.document_type || null,
    allowedCountries: Array.isArray(item.allowed_countries)
      ? item.allowed_countries
      : [],
  }));
};

export const GeneralTab = memo(
  ({
    appId,
    config,
    onConfigChange,
    publicKey,
  }: AppSettingsTabProps & { publicKey: string }) => {
    const { authorizedCompanyId, companies } = useAuth();
    const skipFineTuneSyncRef = useRef(false);
    const [fineTuneLines, setFineTuneLines] = useState<FineTuneLineData[]>(
      mapFineTuneFromConfig(config),
    );

    useEffect(() => {
      if (!config.category) {
        onConfigChange((prev) => {
          if (prev.category) {
            return prev;
          }

          return {
            ...prev,
            category: AppCategory.INDIVIDUAL,
          };
        });
      }
    }, [config.category, onConfigChange]);

    useEffect(() => {
      if (skipFineTuneSyncRef.current) {
        skipFineTuneSyncRef.current = false;
        return;
      }
      setFineTuneLines(mapFineTuneFromConfig(config));
    }, [config]);

    const updateInnovatrics = useCallback(
      (
        updater: (
          current: NonNullable<TOnboardingConfig["innovatrics_settings"]>,
        ) => NonNullable<TOnboardingConfig["innovatrics_settings"]>,
      ) => {
        onConfigChange((prev) => {
          const nextInnovatrics = updater(prev.innovatrics_settings || {});
          return {
            ...prev,
            innovatrics_settings: nextInnovatrics,
          };
        });
      },
      [onConfigChange],
    );

    const syncFineTuneToConfig = useCallback(
      (lines: FineTuneLineData[]) => {
        skipFineTuneSyncRef.current = true;
        updateInnovatrics((innovatrics) => ({
          ...innovatrics,
          document_types: {
            ...(innovatrics.document_types || {
              show_only: [],
              exclude: [],
              fine_tuning: [],
            }),
            fine_tuning: lines
              .filter((line) => line.documentType && line.allowedCountries.length > 0)
              .map((line) => ({
                document_type: line.documentType as string,
                allowed_countries: line.allowedCountries,
              })),
          },
        }));
      },
      [updateInnovatrics],
    );

    const addFineTuneLine = useCallback(() => {
      setFineTuneLines((prev) => {
        const next = [
          ...prev,
          {
            id: `ft-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            documentType: null,
            allowedCountries: [],
          },
        ];
        syncFineTuneToConfig(next);
        return next;
      });
    }, [syncFineTuneToConfig]);

    const updateFineTuneLine = useCallback(
      (updatedLine: FineTuneLineData) => {
        setFineTuneLines((prev) => {
          const next = prev.map((line) =>
            line.id === updatedLine.id ? updatedLine : line,
          );
          syncFineTuneToConfig(next);
          return next;
        });
      },
      [syncFineTuneToConfig],
    );

    const removeFineTuneLine = useCallback(
      (id: string) => {
        setFineTuneLines((prev) => {
          const next = prev.filter((line) => line.id !== id);
          syncFineTuneToConfig(next);
          return next;
        });
      },
      [syncFineTuneToConfig],
    );

    const selectedProvider = config.identification_provider;
    const selectedCategory = config.category || AppCategory.INDIVIDUAL;
    const selectedIndividualsScreeningProvider =
      getSelectedIndividualsScreeningProvider(config);
    const documentRetriesValue = String(
      config.innovatrics_settings?.document_retries ?? "",
    );
    const companyName =
      localStorage.getItem(AUTH_COMPANY_NAME) ||
      companies?.items?.find((company) => company.id === authorizedCompanyId)?.name ||
      "";
    const widgetUrl = useMemo(() => {
      const companyMonolithName = getMonolithString(companyName) || "";
      const baseUrl = WIDGET_URL || "";

      return `${baseUrl}/${companyMonolithName}/?appPublicKey=${publicKey}`;
    }, [companyName, publicKey]);

    return (
      <Box>
        <Stack spacing={1}>
          <GeneralFields
            publicKey={publicKey}
            widgetUrl={widgetUrl}
            category={selectedCategory}
            setCategory={(category: AppCategory) =>
              onConfigChange((prev) => updateCategoryConfig(prev, category))
            }
            identificationProvider={selectedProvider}
            setIdentificationProvider={(provider: IdentificationProvider) =>
              onConfigChange((prev) =>
                updateIdentificationProviderConfig(prev, provider),
              )
            }
            individualsScreeningProvider={selectedIndividualsScreeningProvider}
            setIndividualsScreeningProvider={(
              provider: IndividualsScreeningProvider,
            ) =>
              onConfigChange((prev) => ({
                ...prev,
                individuals_screening_provider: provider,
              }))
            }
            pocStellarVerifiableCreds={config.poc_stellar_verifiable_creds || false}
            setPocStellarVerifiableCreds={(checked: boolean) =>
              onConfigChange((prev) => ({
                ...prev,
                poc_stellar_verifiable_creds: checked,
              }))
            }
          />

          {isProvider(selectedProvider, IdentificationProvider.INNOVATRICS) && (
            <Paper
              sx={{
                margin: "12px 0",
                padding: "20px 32px",
                background: "#ffffff",
                borderRadius: "14px",
                border: "none",
              }}
            >
              <Typography variant='h5' sx={{ mb: 2 }}>
                Innovatrics Settings
              </Typography>
              <InnovatricsSettings
                showOnlyCountries={
                  config.innovatrics_settings?.country_list?.show_only || []
                }
                excludeCountries={
                  config.innovatrics_settings?.country_list?.exclude || []
                }
                showFirstCountries={
                  config.innovatrics_settings?.country_list?.show_first || []
                }
                showOnlyDocTypes={
                  config.innovatrics_settings?.document_types?.show_only || []
                }
                excludeDocTypes={
                  config.innovatrics_settings?.document_types?.exclude || []
                }
                documentRetries={documentRetriesValue}
                fineTuneLines={fineTuneLines}
                onShowOnlyCountriesChange={(value) =>
                  updateInnovatrics((innovatrics) => ({
                    ...innovatrics,
                    country_list: {
                      ...(innovatrics.country_list || {
                        show_only: [],
                        exclude: [],
                        show_first: [],
                      }),
                      show_only: value,
                    },
                  }))
                }
                onExcludeCountriesChange={(value) =>
                  updateInnovatrics((innovatrics) => ({
                    ...innovatrics,
                    country_list: {
                      ...(innovatrics.country_list || {
                        show_only: [],
                        exclude: [],
                        show_first: [],
                      }),
                      exclude: value,
                    },
                  }))
                }
                onShowFirstCountriesChange={(value) =>
                  updateInnovatrics((innovatrics) => ({
                    ...innovatrics,
                    country_list: {
                      ...(innovatrics.country_list || {
                        show_only: [],
                        exclude: [],
                        show_first: [],
                      }),
                      show_first: value,
                    },
                  }))
                }
                onShowOnlyDocTypesChange={(value) =>
                  updateInnovatrics((innovatrics) => ({
                    ...innovatrics,
                    document_types: {
                      ...(innovatrics.document_types || {
                        show_only: [],
                        exclude: [],
                        fine_tuning: [],
                      }),
                      show_only: value,
                    },
                  }))
                }
                onExcludeDocTypesChange={(value) =>
                  updateInnovatrics((innovatrics) => ({
                    ...innovatrics,
                    document_types: {
                      ...(innovatrics.document_types || {
                        show_only: [],
                        exclude: [],
                        fine_tuning: [],
                      }),
                      exclude: value,
                    },
                  }))
                }
                onDocumentRetriesChange={(value) =>
                  updateInnovatrics((innovatrics) => ({
                    ...innovatrics,
                    document_retries: parseInt(value, 10) || 0,
                  }))
                }
                onFineTuneLinesChange={(lines) => {
                  setFineTuneLines(lines);
                  syncFineTuneToConfig(lines);
                }}
                onAddFineTuneLine={addFineTuneLine}
                onUpdateFineTuneLine={updateFineTuneLine}
                onRemoveFineTuneLine={removeFineTuneLine}
              />
            </Paper>
          )}
        </Stack>
      </Box>
    );
  },
);
